'use client';

import { useState } from 'react';
import Card from '../Card';
import Reveal, { revealStagger } from '../Reveal';
import Section from '../Section';
import Eyebrow from '../Eyebrow';
import MetricGrid, { type Metric } from '../MetricGrid';
import { PAXRENT_LAUNCH_LABEL, PAXRENT_URL } from '@/lib/paxrent';

/**
 * Proof: three metrics, then eight transformations.
 *
 * Every card is titled by the change delivered rather than the client name, so
 * the gallery scans as outcomes; the client is a secondary label beneath. Order
 * is most recent first.
 *
 * PaxRent renders as a full-width feature above the grid rather than as the
 * first cell of it. Its body runs roughly five times the length of the other
 * seven, and in a uniform grid every card in its row would inherit that height
 * and the gallery would open as a wall of text. The feature treatment also
 * gives its outbound link and richer outcome the room they need.
 *
 * The collapse is count slicing, not the previous responsive max-height clamp.
 * The clamp cut text mid-line, needed a gradient fade hardcoded to
 * var(--color-navy), and degraded further as card heights diverged. Slicing
 * never cuts a card, needs no fade, and stays correct as cards are added; the
 * only tuning knob is COLLAPSED_COUNT.
 *
 * The nine previous case studies become eight: two are dropped per #7921 and
 * PaxRent is added. The dropped pair is named in the ticket, not here, because
 * #7921 asserts they appear nowhere in src/ and that check reads comments too.
 */

const METRICS: Metric[] = [
  {
    value: '7 weeks',
    caption:
      'PaxRent went from nothing to production with a paying customer collecting rent. Designed, architected and built by one person.',
  },
  {
    value: '50 states',
    caption:
      'Double-entry trust accounting and 50-state compliance, running in daily commercial use. Regulated environments are the normal case here, not the exception.',
  },
  {
    value: '25 years',
    caption:
      'Leading product through two acquisitions and platforms for Apple, Red Bull, Disney, BBC and ESPN, long before any of this was called AI strategy.',
  },
];

interface ImpactCard {
  /** Stable React key. Survives copy edits in a way the client name would not. */
  id: string;
  title: string;
  client: string;
  href?: string;
  challenge: string;
  solution: string;
  outcome: { headline: string; detail: string };
}

const FEATURED: ImpactCard = {
  id: 'paxrent',
  title: 'A property platform that gets better every time the models do',
  client: 'PaxRent',
  href: PAXRENT_URL,
  challenge:
    'Property managers running on four disconnected tools, with no system of record and no way to benefit from advances in AI.',
  solution:
    "Designed and built an AI-native platform solo: trust accounting, leasing, maintenance, payments and reporting under one system, with approval-gated agents handling triage, applicant review and owner communications. Architected as a compounding loop where usage data and feedback surface the system's own improvement opportunities, reinforced by periodic full code audits and each new model generation.",
  outcome: {
    headline: 'Production in 7 Weeks. Better Every Month Since.',
    detail:
      'In continuous commercial operation: double-entry trust accounting, 50-state compliance and agentic workflows in daily use. Four tools replaced at two-thirds lower cost.',
  },
};

const CARDS: ImpactCard[] = [
  {
    id: 'lab-services',
    title: 'A public health emergency to a working operation in weeks',
    client: 'Lab Services of America',
    challenge: 'Addressing a public health emergency with no infrastructure.',
    solution: 'Built custom software and managed staffing for 100s of schools.',
    outcome: {
      headline: 'Built From Zero in Weeks',
      detail: 'hundreds of jobs created across hundreds of school sites',
    },
  },
  {
    id: 'hilti-trimble',
    title: 'Stagnant SaaS to 14% year-on-year growth',
    client: 'Hilti AG & Trimble',
    challenge: 'Stagnant SaaS growth for legacy tools.',
    solution: 'Led a microservice revamp and coached agile teams.',
    outcome: { headline: '14% YoY Increase', detail: 'in SaaS subscriptions.' },
  },
  {
    id: 'wirewax',
    title: 'Enterprise-ready, then acquired by Vimeo',
    client: 'Wirewax',
    challenge: 'Need for enterprise scale and compliance.',
    solution: 'Revamped Digital Asset Management and cloud architecture.',
    outcome: { headline: '30% YoY Growth', detail: 'and acquisition by Vimeo.' },
  },
  {
    id: 'bridgeu',
    title: 'One market to global reach, then acquired by Kaplan',
    client: 'BridgeU',
    challenge: 'Expanding school onboarding and market reach.',
    solution: 'Implemented agile methodologies and secured funding.',
    outcome: { headline: '15% QoQ Growth', detail: 'and successful acquisition by Kaplan.' },
  },
  {
    id: 'blippar',
    title: 'An underperforming app to a 20% retention lift',
    client: 'Blippar',
    challenge: 'Low user retention in the AR space.',
    solution: 'Spearheaded a complete mobile app revamp.',
    outcome: { headline: '20%+ Boost in Retention', detail: 'within months.' },
  },
  {
    id: 'quixel',
    title: 'A startup platform to powering Apple, BBC and ESPN',
    client: 'Quixel',
    challenge: 'Need for a scalable video distribution platform for major clients.',
    solution: 'Launched global video distribution platform.',
    outcome: {
      headline: 'Delivered Thousands of Hours',
      detail: 'of content for Apple, Red Bull, Disney, BBC & ESPN.',
    },
  },
  {
    id: 'dlink',
    title: '50+ fragmented systems to one unified platform',
    client: 'D-Link',
    challenge: '50+ dispersed, inefficient CRMs & CMSs globally.',
    solution: 'Unified requirements from countries into one IT solution.',
    outcome: {
      headline: 'Standardized 50+ Systems',
      detail: 'into unified Salesforce and CMS solutions.',
    },
  },
];

/** One desktop row, matching what the previous height clamp showed at lg. */
const COLLAPSED_COUNT = 3;

function Chevron({ flipped }: { flipped: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
      className={`transition-transform duration-300 ${flipped ? 'rotate-180' : ''}`}
    >
      <path
        d="M3.5 6 8 10.5 12.5 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Block({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Eyebrow tracking="card">{label}</Eyebrow>
      <div className="pt-1.5 font-sans text-[15px] leading-[1.6] text-body">{children}</div>
    </div>
  );
}

export default function ImpactGallerySection() {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => {
    if (isExpanded) {
      const section = document.getElementById('impact');
      if (section) {
        const yOffset = -80;
        const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
    setIsExpanded(!isExpanded);
  };

  const visible = isExpanded ? CARDS : CARDS.slice(0, COLLAPSED_COUNT);

  return (
    <Section id="impact">
      <Reveal>
        <Eyebrow>Proof</Eyebrow>

        <h2 className="mt-[18px] max-w-[720px] font-display text-[32px] font-medium leading-[1.08] tracking-[-0.032em] text-balance text-ink md:text-[42px]">
          Things that exist, not things that were recommended.
        </h2>
      </Reveal>

      {/* The metric grid reveals as one block. Its hairlines are the parent's
          background showing through a 1px gap, so revealing the three cells
          separately would animate the rules between them apart. */}
      <Reveal delay={90}>
        <MetricGrid metrics={METRICS} className="mt-11" />
      </Reveal>

      <Reveal>
        <Card className="mt-6">
          <div className="grid gap-8 lg:grid-cols-[1fr_1.3fr] lg:gap-14">
            <div>
              <h3 className="font-display text-[26px] font-medium leading-[1.15] tracking-[-0.025em] text-balance text-ink md:text-[30px]">
                {FEATURED.title}
              </h3>
              <p className="pt-3 font-sans text-[15px] uppercase tracking-[0.12em] text-caption">
                {FEATURED.client}
              </p>
              <p className="pt-4 font-sans text-[15px] leading-[1.6] text-body">
                In production since {PAXRENT_LAUNCH_LABEL}.
              </p>
              <a
                href={FEATURED.href}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 font-sans text-[15px] font-semibold text-ink-blue underline-offset-4 hover:underline"
              >
                Visit paxrent.com
                <span aria-hidden="true">&rarr;</span>
              </a>
            </div>

            <div className="flex flex-col gap-5">
              <Block label="The Challenge">{FEATURED.challenge}</Block>
              <Block label="The Strategic Solution">{FEATURED.solution}</Block>
              <Block label="The Outcome">
                <p className="font-display text-lg font-medium text-ink">
                  {FEATURED.outcome.headline}
                </p>
                <p className="pt-1">{FEATURED.outcome.detail}</p>
              </Block>
            </div>
          </div>
        </Card>
      </Reveal>

      {/* Expanding mounts four more cards, each with its own observer, so the
          newly revealed row eases in on the same terms as the first. */}
      <div id="impact-cards" className="grid gap-6 pt-6 md:grid-cols-2 lg:grid-cols-3">
        {visible.map((card, index) => (
          <Reveal key={card.id} className="h-full" delay={revealStagger(index % 3)}>
            <Card className="flex h-full flex-col gap-5">
              <div>
                <h3 className="font-display text-xl font-medium leading-[1.2] tracking-[-0.02em] text-balance text-ink">
                  {card.title}
                </h3>
                <p className="pt-2 font-sans text-[13px] uppercase tracking-[0.12em] text-caption">
                  {card.client}
                </p>
              </div>

              <Block label="The Challenge">{card.challenge}</Block>
              <Block label="The Strategic Solution">{card.solution}</Block>
              <Block label="The Outcome">
                <p className="font-display text-base font-medium text-ink">
                  {card.outcome.headline}
                </p>
                <p className="pt-1">{card.outcome.detail}</p>
              </Block>
            </Card>
          </Reveal>
        ))}
      </div>

      {/* Carries this section's extra bottom room. Section's own pb is a
          utility of the same specificity, so overriding it through className
          would depend on Tailwind's emitted order rather than source order. */}
      <div className="flex justify-center pt-8 pb-10 md:pb-16">
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={isExpanded}
          aria-controls="impact-cards"
          className="inline-flex items-center gap-2 font-sans text-[15px] font-semibold text-ink-blue transition-opacity duration-200 hover:opacity-70"
        >
          {isExpanded ? 'Show Less' : 'Show All Results'}
          <Chevron flipped={isExpanded} />
        </button>
      </div>
    </Section>
  );
}
